import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const ProfessionalCard = ({ professional }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const categoryColors = {
    lawyer: { bg: 'rgba(123,47,255,0.15)', border: 'rgba(123,47,255,0.4)', text: '#A78BFA', icon: 'Scale' },
    photographer: { bg: 'rgba(0,212,255,0.15)', border: 'rgba(0,212,255,0.4)', text: '#00D4FF', icon: 'Camera' },
    stylist: { bg: 'rgba(255,107,157,0.15)', border: 'rgba(255,107,157,0.4)', text: '#FF6B9D', icon: 'Palette' },
  };

  const colors = categoryColors?.[professional?.category] || categoryColors?.lawyer;

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <span key={i} style={{ color: i < Math.floor(rating) ? '#FFD700' : 'rgba(255,215,0,0.3)', fontSize: '14px' }}>★</span>
    ));

  return (
    <div
      className="rounded-xl p-5 cursor-pointer transition-all duration-300 hover:scale-[1.02]"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,212,255,0.15)', backdropFilter: 'blur(10px)' }}
      onClick={() => navigate(`/professional-profile?id=${professional?.id}`)}
    >
      <div className="flex items-start gap-4 mb-4">
        <div
          className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl font-bold"
          style={{ background: colors?.bg, border: `1px solid ${colors?.border}`, color: colors?.text }}
        >
          {professional?.avatar
            ? <img src={professional?.avatar} alt={`${professional?.name} profile photo`} className="w-full h-full rounded-xl object-cover" />
            : professional?.name?.charAt(0)?.toUpperCase()
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-white text-base truncate">{professional?.name}</h3>
            {professional?.verified && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(0,212,255,0.15)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' }}>
                <Icon name="BadgeCheck" size={11} /> {t('verified')}
              </span>
            )}
          </div>
          <p className="text-sm mt-0.5" style={{ color: '#6B9EC4' }}>{professional?.company}</p>
          <div className="flex items-center gap-1 mt-1">
            <Icon name="MapPin" size={12} color="#6B9EC4" />
            <span className="text-xs" style={{ color: '#6B9EC4' }}>{professional?.location}</span>
          </div>
        </div>
        <div className="px-2.5 py-1 rounded-lg text-xs font-semibold flex-shrink-0" style={{ background: colors?.bg, color: colors?.text, border: `1px solid ${colors?.border}` }}>
          {professional?.categoryLabel}
        </div>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex">{renderStars(professional?.rating)}</div>
        <span className="text-sm font-semibold text-white">{professional?.rating?.toFixed(1)}</span>
        <span className="text-xs" style={{ color: '#6B9EC4' }}>({professional?.reviewCount} {t('reviews')})</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {professional?.specializations?.slice(0, 3)?.map((spec, i) => (
          <span key={i} className="px-2 py-0.5 rounded-md text-xs" style={{ background: 'rgba(255,255,255,0.06)', color: '#A0B4C8', border: '1px solid rgba(255,255,255,0.08)' }}>
            {spec}
          </span>
        ))}
        {professional?.specializations?.length > 3 && (
          <span className="px-2 py-0.5 rounded-md text-xs" style={{ background: 'rgba(255,255,255,0.06)', color: '#6B9EC4' }}>+{professional?.specializations?.length - 3} more</span>
        )}
      </div>
      <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div>
          <span className="text-xs" style={{ color: '#6B9EC4' }}>{t('startingFrom')}</span>
          <p className="text-base font-bold" style={{ color: '#00D4FF' }}>{professional?.startingPrice}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={(e) => e?.stopPropagation()}
            className="p-2 rounded-lg transition-all duration-200"
            style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)', color: '#00D4FF' }}
          >
            <Icon name="MessageCircle" size={16} />
          </button>
          <button
            onClick={(e) => { e?.stopPropagation(); navigate(`/professional-profile?id=${professional?.id}`); }}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
            style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000' }}
          >
            {t('viewProfile')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalCard;
